import React, { useEffect, useCallback, useState } from "react";
import { FaLinkedin, FaTwitter, FaGithub } from "react-icons/fa";
import { SiOpenai } from "react-icons/si";
import { motion, useAnimation, useMotionValue, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { TypeAnimation } from "react-type-animation";
import { ParallaxScroll } from "./ScrollAnimation";
import { useIsMobile } from "../hooks/useIsMobile";

/* Neural network connection line — smooth, no opacity blink */
const NeuralLine = ({ x1, y1, x2, y2, delay }: { x1: string; y1: string; x2: string; y2: string; delay: number }) => (
  <motion.line
    x1={x1} y1={y1} x2={x2} y2={y2}
    stroke="currentColor"
    strokeWidth="0.8"
    className="text-primary-500/10 dark:text-primary-400/10"
    initial={{ pathLength: 0 }}
    animate={{ pathLength: [0, 1, 0] }}
    transition={{ duration: 6, repeat: Infinity, delay, ease: "easeInOut" }}
  />
);

/* Floating geometric particles — smooth movement only, no opacity changes */
const FloatingParticle = ({ delay, x, y, size, duration }: { delay: number; x: string; y: string; size: number; duration: number }) => (
  <motion.div
    className="absolute rounded-full bg-primary-500/10 dark:bg-primary-400/8"
    style={{ left: x, top: y, width: size, height: size }}
    animate={{
      y: [0, -40, 0],
      x: [0, 20, 0],
      scale: [1, 1.3, 1],
    }}
    transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
  />
);

/* Neural node — smooth scale only, no opacity blink */
const NeuralNode = ({ x, y, size, delay }: { x: string; y: string; size: number; delay: number }) => (
  <motion.div
    className="absolute rounded-full bg-primary-500/20 dark:bg-primary-400/15 border border-primary-500/20 dark:border-primary-400/15"
    style={{ left: x, top: y, width: size, height: size }}
    animate={{ scale: [1, 1.5, 1] }}
    transition={{ duration: 4, repeat: Infinity, delay, ease: "easeInOut" }}
  />
);


const HeroSection: React.FC = () => {
  const controls = useAnimation();
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [isMounted, setIsMounted] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [controls, inView]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile) return;
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      mouseX.set((e.clientX - rect.left - rect.width / 2) / 40);
      mouseY.set((e.clientY - rect.top - rect.height / 2) / 40);
    },
    [mouseX, mouseY, isMobile]
  );

  const bgX = useTransform(mouseX, (v) => v * -0.5);
  const bgY = useTransform(mouseY, (v) => v * -0.5);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.4, staggerChildren: 0.12, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, filter: "blur(10px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.7, rotate: -5 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: { duration: 1, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  return (
    <section
      className={`relative flex items-center overflow-hidden ${isMobile ? 'min-h-screen py-20' : 'min-h-[115vh]'}`}
      id="home"
      ref={ref}
      onMouseMove={handleMouseMove}
    >
      {/* Animated background layer — desktop only for performance */}
      {!isMobile ? (
        <motion.div className="absolute inset-0 overflow-hidden" style={{ x: bgX, y: bgY }}>
          {/* Massive morphing blobs with vivid AI-themed colors and parallax */}
          <ParallaxScroll offset={20}>
            <div className="absolute -top-48 -right-48 w-[900px] h-[900px] bg-gradient-to-br from-primary-500/15 via-violet-500/10 to-cyan-500/8 dark:from-primary-500/10 dark:via-violet-500/6 dark:to-cyan-500/4 rounded-full blur-[120px] animate-blob" />
          </ParallaxScroll>
          <ParallaxScroll offset={-30}>
            <div className="absolute top-1/3 -left-64 w-[1000px] h-[1000px] bg-gradient-to-tr from-accent-500/12 via-cyan-500/8 to-blue-500/6 dark:from-accent-500/7 dark:via-cyan-500/4 dark:to-blue-500/3 rounded-full blur-[140px] animate-blob-slow animation-delay-2000" />
          </ParallaxScroll>
          <ParallaxScroll offset={15}>
            <div className="absolute -bottom-48 right-1/4 w-[800px] h-[800px] bg-gradient-to-tl from-primary-400/12 via-blue-500/8 to-violet-500/6 dark:from-primary-400/7 dark:via-blue-500/4 dark:to-violet-500/3 rounded-full blur-[120px] animate-blob animation-delay-4000" />
          </ParallaxScroll>
          <ParallaxScroll offset={-20}>
            <div className="absolute top-1/4 right-1/4 w-[700px] h-[700px] bg-gradient-to-br from-violet-400/10 via-pink-400/6 to-accent-400/5 dark:from-violet-400/6 dark:via-pink-400/3 dark:to-accent-400/2 rounded-full blur-[100px] animate-morph animation-delay-3000" />
          </ParallaxScroll>
          <ParallaxScroll offset={25}>
            <div className="absolute bottom-1/4 left-1/3 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-400/8 via-primary-400/6 to-violet-400/4 dark:from-cyan-400/5 dark:via-primary-400/3 dark:to-violet-400/2 rounded-full blur-[100px] animate-blob animation-delay-1000" />
          </ParallaxScroll>

          {/* Neural network SVG overlay */}
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <NeuralLine x1="10%" y1="20%" x2="30%" y2="35%" delay={0} />
            <NeuralLine x1="30%" y1="35%" x2="55%" y2="25%" delay={0.8} />
            <NeuralLine x1="55%" y1="25%" x2="80%" y2="40%" delay={1.6} />
            <NeuralLine x1="80%" y1="40%" x2="90%" y2="20%" delay={2.4} />
            <NeuralLine x1="15%" y1="60%" x2="40%" y2="70%" delay={3} />
            <NeuralLine x1="40%" y1="70%" x2="65%" y2="55%" delay={3.8} />
            <NeuralLine x1="65%" y1="55%" x2="85%" y2="70%" delay={4.5} />
            <NeuralLine x1="30%" y1="35%" x2="40%" y2="70%" delay={1.2} />
            <NeuralLine x1="55%" y1="25%" x2="65%" y2="55%" delay={2} />
            <NeuralLine x1="20%" y1="85%" x2="50%" y2="90%" delay={3.5} />
            <NeuralLine x1="50%" y1="90%" x2="75%" y2="80%" delay={4.2} />
          </svg>

          {/* Neural nodes */}
          {isMounted && (
            <>
              <NeuralNode x="10%" y="20%" size={10} delay={0} />
              <NeuralNode x="30%" y="35%" size={8} delay={0.8} />
              <NeuralNode x="55%" y="25%" size={12} delay={1.6} />
              <NeuralNode x="80%" y="40%" size={9} delay={2.4} />
              <NeuralNode x="15%" y="60%" size={8} delay={3} />
              <NeuralNode x="40%" y="70%" size={10} delay={3.8} />
              <NeuralNode x="65%" y="55%" size={11} delay={2} />
              <NeuralNode x="85%" y="70%" size={8} delay={4.5} />
              <NeuralNode x="50%" y="90%" size={9} delay={3.5} />
            </>
          )}

          {/* Floating particles */}
          {isMounted && (
            <>
              <FloatingParticle delay={0} x="10%" y="20%" size={8} duration={5} />
              <FloatingParticle delay={1} x="85%" y="15%" size={6} duration={6} />
              <FloatingParticle delay={2} x="70%" y="70%" size={10} duration={7} />
              <FloatingParticle delay={1.5} x="20%" y="80%" size={7} duration={5.5} />
              <FloatingParticle delay={0.5} x="50%" y="10%" size={5} duration={6.5} />
              <FloatingParticle delay={2.5} x="90%" y="50%" size={8} duration={5.8} />
            </>
          )}

          {/* Dot grid — slightly denser */}
          <div
            className="absolute inset-0 opacity-[0.04] dark:opacity-[0.07]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
              backgroundSize: "35px 35px",
            }}
          />
        </motion.div>
      ) : (
        /* Mobile: simple subtle gradient background */
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-gradient-to-br from-primary-500/10 via-violet-500/5 to-transparent rounded-full blur-[80px]" />
          <div className="absolute -bottom-32 -left-32 w-[350px] h-[350px] bg-gradient-to-tr from-accent-500/8 via-cyan-500/4 to-transparent rounded-full blur-[80px]" />
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 w-full">
        <div className={`flex flex-col lg:flex-row items-center justify-between ${isMobile ? 'gap-10' : 'gap-16 lg:gap-24'}`}>
          {/* Text Content */}
          <motion.div
            className="flex-1 text-center lg:text-left max-w-2xl"
            variants={containerVariants}
            initial="hidden"
            animate={controls}
          >
            {/* Status badge */}
            <motion.div variants={itemVariants}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50/80 dark:bg-primary-950/50 border border-primary-200/60 dark:border-primary-800/60 backdrop-blur-sm mb-8">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent-500" />
                </span>
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  Open to AI Engineering roles
                </span>
              </div>
            </motion.div>

            {/* Main heading */}
            <motion.h1
              className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight"
              variants={itemVariants}
            >
              Hi, I'm{" "}
              <span className="gradient-text inline-block">RIFATH</span>
            </motion.h1>

            {/* Type animation */}
            <motion.div variants={itemVariants} className="mt-5 h-10">
              <TypeAnimation
                sequence={[
                  "AI Engineer",
                  2500,
                  "Machine Learning Engineer",
                  2500,
                  "Full Stack Developer",
                  2500,
                  "LLM & NLP Specialist",
                  2500,
                  "Computer Vision Engineer",
                  2500,
                ]}
                speed={40}
                className="text-xl sm:text-2xl lg:text-3xl font-semibold text-primary-600 dark:text-primary-400"
                repeat={Infinity}
              />
            </motion.div>

            {/* Description */}
            <motion.p
              className="mt-7 text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-xl mx-auto lg:mx-0"
              variants={itemVariants}
            >
              AI Engineer passionate about building intelligent systems. I specialize in
              machine learning, deep learning, NLP, and full-stack development to create
              AI-powered applications that solve real-world problems.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              variants={itemVariants}
            >
              <motion.button
                className="btn-primary group"
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                onClick={() =>
                  window.open("https://www.fiverr.com/s/5rdyNgk", "_blank")
                }
              >
                Let's Work Together
                <motion.svg
                  className="w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </motion.svg>
              </motion.button>

              <motion.button
                className="btn-secondary group"
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  const el = document.getElementById("projects");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View My Work
              </motion.button>
            </motion.div>

            {/* Social Links */}
            <motion.div
              className={`${isMobile ? 'mt-8' : 'mt-14'} flex items-center gap-5 justify-center lg:justify-start`}
              variants={itemVariants}
            >
              <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-widest">
                Follow
              </span>
              <div className="w-10 h-px bg-gradient-to-r from-gray-300 to-transparent dark:from-gray-700" />
              {[
                { icon: <FaLinkedin size={18} />, link: "https://www.linkedin.com", label: "LinkedIn" },
                { icon: <FaTwitter size={18} />, link: "https://www.twitter.com", label: "Twitter" },
                { icon: <FaGithub size={18} />, link: "https://www.github.com", label: "GitHub" },
              ].map((social, index) => (
                <motion.a
                  key={index}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="relative p-3 rounded-xl text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/50 transition-all duration-300 group focus-visible-ring"
                  whileHover={{ y: -4, scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {social.icon}
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-medium text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {social.label}
                  </span>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          {/* Profile Image — Big Standing Portrait */}
          <motion.div
            className="relative flex-shrink-0"
            variants={imageVariants}
            initial="hidden"
            animate={controls}
          >
            <div className={`relative ${isMobile ? 'w-[280px]' : 'w-[22rem] sm:w-[480px] lg:w-[650px]'}`}>

              {/* AI Animations behind the image — desktop only */}
              {!isMobile && (
                <div className="absolute inset-0 -inset-x-10 -inset-y-6 z-0 overflow-visible">
                  {/* Neural network SVG lines */}
                  <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    {/* Circuit traces */}
                    <motion.path
                      d="M 10% 20% L 30% 15% L 50% 25% L 70% 10% L 90% 20%"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      className="text-primary-500/20 dark:text-primary-400/20"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: [0, 1, 0] }}
                      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.path
                      d="M 5% 80% L 25% 75% L 45% 85% L 65% 70% L 95% 80%"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      className="text-violet-500/20 dark:text-violet-400/15"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: [0, 1, 0] }}
                      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                    />
                    <motion.path
                      d="M 20% 40% L 40% 50% L 60% 40% L 80% 55%"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="0.8"
                      className="text-cyan-500/15 dark:text-cyan-400/12"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: [0, 1, 0] }}
                      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 3 }}
                    />
                    {/* Vertical data stream lines */}
                    <motion.line x1="15%" y1="0%" x2="15%" y2="100%" stroke="currentColor" strokeWidth="0.5" className="text-primary-500/10 dark:text-primary-400/10" strokeDasharray="4 8"
                      animate={{ strokeDashoffset: [0, -48] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    />
                    <motion.line x1="85%" y1="0%" x2="85%" y2="100%" stroke="currentColor" strokeWidth="0.5" className="text-violet-500/10 dark:text-violet-400/10" strokeDasharray="4 8"
                      animate={{ strokeDashoffset: [0, 48] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    />
                    <motion.line x1="50%" y1="0%" x2="50%" y2="100%" stroke="currentColor" strokeWidth="0.3" className="text-accent-500/8 dark:text-accent-400/8" strokeDasharray="6 12"
                      animate={{ strokeDashoffset: [0, -72] }}
                      transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                    />
                  </svg>

                  {/* Floating neural nodes */}
                  <motion.div
                    className="absolute w-3 h-3 rounded-full border border-primary-500/30 dark:border-primary-400/25 bg-primary-500/10"
                    style={{ left: "10%", top: "15%" }}
                    animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0.8, 0.4] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <motion.div
                    className="absolute w-2.5 h-2.5 rounded-full border border-violet-500/30 dark:border-violet-400/25 bg-violet-500/10"
                    style={{ left: "88%", top: "25%" }}
                    animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.7, 0.3] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                  />
                  <motion.div
                    className="absolute w-2 h-2 rounded-full border border-cyan-500/30 dark:border-cyan-400/25 bg-cyan-500/10"
                    style={{ left: "70%", top: "10%" }}
                    animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                  />
                  <motion.div
                    className="absolute w-3 h-3 rounded-full border border-accent-500/25 dark:border-accent-400/20 bg-accent-500/10"
                    style={{ left: "5%", top: "70%" }}
                    animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.7, 0.3] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                  />
                  <motion.div
                    className="absolute w-2 h-2 rounded-full border border-primary-500/25 dark:border-primary-400/20 bg-primary-500/10"
                    style={{ left: "92%", top: "65%" }}
                    animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0.8, 0.4] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  />

                  {/* Floating code/data snippets */}
                  <motion.div
                    className="absolute text-[10px] font-mono text-primary-500/20 dark:text-primary-400/15 whitespace-nowrap"
                    style={{ left: "-5%", top: "30%" }}
                    animate={{ y: [0, -20, 0], opacity: [0.15, 0.35, 0.15] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    {"model.predict()"}
                  </motion.div>
                  <motion.div
                    className="absolute text-[10px] font-mono text-violet-500/20 dark:text-violet-400/15 whitespace-nowrap"
                    style={{ right: "-8%", top: "50%" }}
                    animate={{ y: [0, 15, 0], opacity: [0.15, 0.3, 0.15] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                  >
                    {"torch.nn.Linear"}
                  </motion.div>
                  <motion.div
                    className="absolute text-[10px] font-mono text-cyan-500/20 dark:text-cyan-400/12 whitespace-nowrap"
                    style={{ left: "-3%", top: "85%" }}
                    animate={{ y: [0, -12, 0], opacity: [0.1, 0.25, 0.1] }}
                    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 3.5 }}
                  >
                    {"transformer.forward()"}
                  </motion.div>
                  <motion.div
                    className="absolute text-[10px] font-mono text-primary-500/15 dark:text-primary-400/10 whitespace-nowrap"
                    style={{ right: "-5%", top: "88%" }}
                    animate={{ y: [0, 10, 0], opacity: [0.1, 0.2, 0.1] }}
                    transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  >
                    {"loss.backward()"}
                  </motion.div>

                  {/* Orbiting data particles */}
                  <motion.div
                    className="absolute w-1.5 h-1.5 rounded-full bg-primary-500/30 dark:bg-primary-400/25"
                    style={{ left: "50%", top: "5%" }}
                    animate={{ x: [0, 60, 0, -60, 0], y: [0, 30, 60, 30, 0] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <motion.div
                    className="absolute w-1 h-1 rounded-full bg-violet-500/30 dark:bg-violet-400/25"
                    style={{ left: "50%", top: "95%" }}
                    animate={{ x: [0, -50, 0, 50, 0], y: [0, -25, -50, -25, 0] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                  />

                  {/* Binary/hex data streams */}
                  <motion.div
                    className="absolute text-[8px] font-mono text-primary-500/10 dark:text-primary-400/8 leading-tight"
                    style={{ left: "2%", top: "45%" }}
                    animate={{ opacity: [0.05, 0.15, 0.05] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    {"01101\n10010\n01110"}
                  </motion.div>
                  <motion.div
                    className="absolute text-[8px] font-mono text-violet-500/10 dark:text-violet-400/8 leading-tight"
                    style={{ right: "2%", top: "35%" }}
                    animate={{ opacity: [0.05, 0.15, 0.05] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                  >
                    {"0xF7A\n0xB3E\n0x91C"}
                  </motion.div>
                </div>
              )}

              {/* Main image */}
              <div className="relative z-[1]">
                <img
                  src="/profileimg_1.png"
                  alt="RIFATH MFM"
                  className={`w-full h-auto object-cover object-top ${isMobile ? 'rounded-2xl' : ''}`}
                  style={{ minHeight: isMobile ? 'auto' : '680px' }}
                />
              </div>

              {/* Floating badges — static on mobile, animated on desktop */}
              {isMobile ? (
                <>
                  <div className="absolute -right-2 top-4 glass-card px-3 py-2 !rounded-xl shadow-lg z-10">
                    <div className="flex items-center gap-1.5">
                      <SiOpenai className="text-primary-500 text-sm" />
                      <div>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400">AI</p>
                        <p className="text-xs font-bold text-gray-900 dark:text-white">Engineer</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -left-2 bottom-16 glass-card px-3 py-2 !rounded-xl shadow-lg z-10">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">🧠</span>
                      <span className="text-xs font-bold text-gray-900 dark:text-white">Deep Learning</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Floating badge - AI Engineer */}
                  <motion.div
                    className="absolute -right-5 top-8 glass-card px-5 py-3 !rounded-2xl shadow-xl z-10"
                    animate={{ y: [0, -12, 0], rotate: [0, 2, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className="flex items-center gap-2">
                      <SiOpenai className="text-primary-500 text-lg" />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">AI</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">Engineer</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Floating badge - Deep Learning */}
                  <motion.div
                    className="absolute -left-5 top-1/3 glass-card px-4 py-3 !rounded-2xl shadow-xl z-10"
                    animate={{ y: [0, 12, 0], rotate: [0, -3, 0] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🧠</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">Deep Learning</span>
                    </div>
                  </motion.div>

                  {/* Floating badge - bottom */}
                  <motion.div
                    className="absolute left-1/2 -translate-x-1/2 -bottom-5 glass-card px-4 py-2 !rounded-xl shadow-xl z-10"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">🚀</span>
                      <span className="text-xs font-bold text-gray-900 dark:text-white">Building the Future</span>
                    </div>
                  </motion.div>

                  {/* Corner accent dots */}
                  <motion.div
                    className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-accent-500 shadow-lg shadow-accent-500/50 z-10"
                    animate={{ scale: [1, 1.4, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <motion.div
                    className="absolute -top-2 -left-2 w-3 h-3 rounded-full bg-primary-500 shadow-lg shadow-primary-500/50 z-10"
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                  />
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator — hidden on mobile */}
      {!isMobile && (
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 0.8 }}
        >
          <motion.div
            className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500 cursor-pointer group"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            onClick={() => {
              const el = document.getElementById("about");
              el?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] group-hover:text-primary-500 transition-colors">
              Scroll Down
            </span>
            <div className="w-5 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 flex justify-center group-hover:border-primary-500 transition-colors">
              <motion.div
                className="w-1 h-2 mt-1.5 rounded-full bg-gray-400 dark:bg-gray-500 group-hover:bg-primary-500"
                animate={{ y: [0, 8, 0], opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default HeroSection;
